import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { useIntl } from "react-intl";

import CustomFieldDataType from "@mapmycustomers/shared/enum/CustomFieldDataType";
import OptionValue from "@mapmycustomers/shared/types/customField/OptionValue";
import { CardCollapse, CollapsePanel, TextField } from "@mapmycustomers/ui";

import CurrencySelect from "@app/component/CurrencySelect";
import Checkbox from "@app/component/input/Checkbox";
import { setCustomFieldEditStep } from "@app/scene/settings/component/CustomFields/store/actions";
import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import isDataTypeOptionable from "@app/scene/settings/component/CustomFields/util/isDataTypeOptionable";
import FieldRequiredAlert from "@app/scene/settings/component/ManageFields/component/FieldRequiredAlert";
import Options from "@app/scene/settings/component/ManageFields/component/Options";
import CreateEditCustomFieldStep from "@app/scene/settings/enum/CreateEditCustomFieldStep";
import canBeUnique from "@app/scene/settings/util/canBeUnique";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import CustomFieldTest from "../CustomFieldTest";
import NumberFormat from "../NumberFormatType";
import StepControl from "../StepControl";

import styles from "./FieldSetup.module.scss";
import messages from "./messages";

interface Props {
  analyticIssuer?: AnalyticsService;
  form: FormInstance<DefaultCustomFieldFormValues>;
  setStep: typeof setCustomFieldEditStep;
}

const FieldSetup: React.FC<Props> = ({ analyticIssuer, form, setStep }) => {
  const intl = useIntl();
  const isUnique = useWatch("isUnique", form);
  const dataType = useWatch("dataType", form);
  const displayType = useWatch("displayType", form);

  useEffect(() => {
    analyticIssuer?.clicked(["Field Setup"]);
  }, [analyticIssuer]);

  const handleBackStepClick = useCallback(() => {
    setStep(CreateEditCustomFieldStep.BASIC_INFO);
  }, [setStep]);

  const handleNextStepClick = useCallback(async () => {
    try {
      const { options } = form.getFieldsValue();

      await form.validateFields([
        "currencyId",
        "displayName",
        ...(options ?? []).map((_, index) => ["options", index, "displayName"]),
      ]);

      setStep(CreateEditCustomFieldStep.SHARE_SAVE);
    } catch {
      // do nothing
    }
  }, [form, setStep]);

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <Form.Item
          name="displayName"
          rules={[{ message: intl.formatMessage(messages.error), required: true }]}
        >
          <TextField label={intl.formatMessage(messages.title)} size="large" />
        </Form.Item>

        <Form.Item dependencies={["dataType"]} noStyle>
          {({ getFieldValue }) => {
            const dataType = getFieldValue("dataType");

            switch (dataType) {
              case CustomFieldDataType.DECIMAL:
              case CustomFieldDataType.INTEGER:
                return (
                  <Form.Item name="displayType">
                    <NumberFormat label={intl.formatMessage(messages.numericFormatTitle)} />
                  </Form.Item>
                );

              case CustomFieldDataType.LARGE_TEXT:
              case CustomFieldDataType.TEXT:
                return (
                  <Form.Item name="placeholder">
                    <TextField
                      label={intl.formatMessage(messages.placeholderTitle)}
                      placeholder={intl.formatMessage(messages.placeholder)}
                      size="large"
                    />
                  </Form.Item>
                );

              case CustomFieldDataType.MONETARY:
                return (
                  <Form.Item
                    name="currencyId"
                    rules={[
                      { message: intl.formatMessage(messages.currencyError), required: true },
                    ]}
                  >
                    <CurrencySelect
                      label={intl.formatMessage(messages.currencyTitle)}
                      size="large"
                      useCurrencyId
                    />
                  </Form.Item>
                );
            }
          }}
        </Form.Item>

        {canBeUnique(dataType, displayType) && (
          <Form.Item name="isUnique" valuePropName="checked">
            <Checkbox>{intl.formatMessage(messages.markUnique)}</Checkbox>
          </Form.Item>
        )}
        <FieldRequiredAlert isCreateForm isUnique={isUnique} />

        <hr />

        <Form.Item className={styles.customField} shouldUpdate>
          {({ getFieldsValue, getFieldValue }) => {
            const currencyId = getFieldValue("currencyId");
            const displayName = getFieldValue("displayName");
            const displayType = getFieldValue("displayType");
            const hasLabel = displayName.trim().length > 0;
            const placeholder = getFieldValue("placeholder");

            const customField = getFieldsValue();
            const options = customField.options
              ? customField.options.filter(({ value }: OptionValue) => value)
              : [];

            return (
              <CardCollapse size="small">
                <CollapsePanel
                  header={intl.formatMessage(messages.customFieldPreviewTitle)}
                  key="preview"
                >
                  <div className={styles.customFieldDesc}>
                    {intl.formatMessage(messages.customFieldPreviewHint, {
                      u: (text: string) => <u>{text}</u>,
                    })}
                  </div>

                  <CustomFieldTest
                    customField={{
                      ...customField,
                      currencyId,
                      displayType,
                      options,
                      placeholder,
                    }}
                    label={hasLabel ? displayName : undefined}
                  />
                </CollapsePanel>
              </CardCollapse>
            );
          }}
        </Form.Item>

        <Form.Item dependencies={["dataType"]}>
          {({ getFieldValue }) =>
            isDataTypeOptionable(getFieldValue("dataType")) && <Options form={form} />
          }
        </Form.Item>
      </div>

      <StepControl onBack={handleBackStepClick} onNext={handleNextStepClick} />
    </div>
  );
};

const mapDispatchToProps = {
  setStep: setCustomFieldEditStep,
};

export default connect(null, mapDispatchToProps)(FieldSetup);
